import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography, IconButton
} from "@mui/material";
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import useAuth from "../../hooks/store/auth";
import { Layout as AuthLayout } from "../../common/Layout/auth/layout";
import { useNavigate } from "react-router-dom";

const Page = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetEmail = searchParams.get("email");
  const resetCode = searchParams.get("code");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { updateAction, loading } = useAuth();

  React.useEffect(() => {
    if (!resetEmail || !resetCode) {
      navigate("/");
    }
  }, [navigate, resetCode, resetEmail]);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirm: "",
      submit: null,
    },
    validationSchema: Yup.object().shape({
      password: Yup.string().max(255).required("Password is required"),
      confirm: Yup.string()
        .max(255)
        .required("Password is required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
    onSubmit: async (values, helpers) => {
      const result = await updateAction({
        email: resetEmail,
        code: resetCode,
        password: values.password,
        confirm: values.confirm,
        redirectTo: "/",
        logoutAfterSuccess: true,
      });

      if (result?.type === "auth/update/fulfilled") {
        navigate("/");
        return;
      }

      helpers.setStatus({ success: false });
      helpers.setErrors({
        submit: result?.payload || "Unable to update password",
      });
      helpers.setSubmitting(false);
    },
  });

  return (
    <AuthLayout>
      <Box
        sx={{
          flex: "1 1 auto",
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: "100px",
            width: "100%",
          }}
        >
          <div>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <Typography variant="h4">Update Password</Typography>
            </Stack>
            <form noValidate onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  error={!!(formik.touched.password && formik.errors.password)}
                  fullWidth
                  helperText={formik.touched.password && formik.errors.password}
                  label="Password"
                  name="password"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  error={!!(formik.touched.confirm && formik.errors.confirm)}
                  fullWidth
                  helperText={formik.touched.confirm && formik.errors.confirm}
                  label="Confirm Password"
                  name="confirm"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.confirm}
                  type={showConfirmPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              {formik.errors.submit && (
                <Typography color="error" sx={{ mt: 3 }} variant="body2">
                  {formik.errors.submit}
                </Typography>
              )}
              <Button
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
                variant="contained"
              >
                {loading ? (
                  <CircularProgress size={25} style={{ color: "#0b3b4b" }} />
                ) : (
                  "Update Password"
                )}
              </Button>
            </form>
          </div>
        </Box>
      </Box>
    </AuthLayout>
  );
};

Page.getLayout = (page) => <AuthLayout>{page}</AuthLayout>;

export default Page;
